import {
  discoveryApiRef,
  fetchApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useMemo } from 'react';

import type {
  AlertmanagerAlert,
  ApiResourcesResponse,
  AlertmanagerCapability,
  AlertmanagerMatcher,
  AlertmanagerSilence,
  CanIResponse,
  CanISpec,
  FavoriteResource,
  ImportResponse,
  K8sAlertsResponse,
  K8sClustersResponse,
  K8sList,
  K8sObject,
  K8sWatchEvent,
  NamespaceDashboardResponse,
  PagedList,
  Preferences,
} from './types';

/** Objects requested per page when sweeping a list path. */
export const LIST_PAGE_SIZE = 500;
/**
 * Pages a single sweep will follow before stopping and reporting truncation.
 * 10 × 500 keeps the worst case bounded while covering every namespace we
 * actually run.
 */
export const LIST_MAX_PAGES = 10;

/** Origin used only to parse/rebuild relative API paths; never requested. */
const PATH_ONLY_BASE = 'https://kubernetes.invalid';

// The Azure Kubernetes Service AAD Server first-party application. Access
// tokens minted for this scope are accepted directly by AKS API servers with
// Entra ID integration — the token requested by the AKS API server scope.
export const AKS_SERVER_APP_SCOPE =
  '6dae42f8-4368-4678-94ff-3960e28e3630/user.read';

interface MicrosoftAccessTokenApi {
  getAccessToken(scope?: string | string[]): Promise<string>;
}

export class KubernetesConsoleApi {
  constructor(
    private readonly discoveryApi: {
      getBaseUrl(pluginId: string): Promise<string>;
    },
    private readonly fetchApi: {
      fetch(input: RequestInfo, init?: RequestInit): Promise<Response>;
    },
    private readonly microsoftAuthApi: MicrosoftAccessTokenApi,
  ) {}

  async clusters(): Promise<K8sClustersResponse> {
    return this.requestJson<K8sClustersResponse>('/native/clusters');
  }

  /** GET a Kubernetes API path on a cluster, parsed as JSON. */
  async proxy<T = K8sList>(cluster: string, path: string): Promise<T> {
    const response = await this.request(this.proxyPath(cluster, path));
    return response.json() as Promise<T>;
  }

  /**
   * GET every object at a Kubernetes list path, following `metadata.continue`
   * until the API server runs out or `maxPages` is reached.
   *
   * A bare `?limit=N` request truncates without saying so, which makes both
   * list pages and the counts derived from them quietly wrong. This returns a
   * `continueToken` instead when it stops early, so callers can say "showing
   * N of more" and offer to fetch the rest.
   */
  async listAll(
    cluster: string,
    path: string,
    options: {
      pageSize?: number;
      maxPages?: number;
      continueToken?: string;
    } = {},
  ): Promise<PagedList> {
    const pageSize = options.pageSize ?? LIST_PAGE_SIZE;
    const maxPages = options.maxPages ?? LIST_MAX_PAGES;
    const items: K8sObject[] = [];
    let token = options.continueToken;

    for (let page = 0; page < maxPages; page += 1) {
      const url = new URL(path, PATH_ONLY_BASE);
      url.searchParams.set('limit', String(pageSize));
      if (token) url.searchParams.set('continue', token);
      // eslint-disable-next-line no-await-in-loop -- pages are sequential by design
      const list = await this.proxy<K8sList>(
        cluster,
        `${url.pathname}${url.search}`,
      );
      items.push(...(list.items ?? []));
      token = list.metadata?.continue || undefined;
      if (!token) return { items };
      if (page === maxPages - 1) {
        return {
          items,
          continueToken: token,
          remaining: list.metadata?.remainingItemCount,
        };
      }
    }
    return { items };
  }

  /** GET a Kubernetes API path that returns plain text (pod logs). */
  async proxyText(cluster: string, path: string): Promise<string> {
    const response = await this.request(this.proxyPath(cluster, path));
    return response.text();
  }

  /**
   * Every resource type the cluster serves, CRDs included. Cached server-side
   * for a few minutes — the catalogue only moves when operators do.
   */
  async apiResources(cluster: string): Promise<ApiResourcesResponse> {
    return this.requestJson<ApiResourcesResponse>(
      `/native/clusters/${encodeURIComponent(cluster)}/api-resources`,
    );
  }

  async canI(cluster: string, spec: CanISpec): Promise<CanIResponse> {
    return this.requestJson<CanIResponse>(
      `/native/clusters/${encodeURIComponent(cluster)}/can-i`,
      { method: 'POST', body: JSON.stringify(spec) },
    );
  }

  /**
   * Create the objects in a YAML document (multi-doc supported). Namespaced
   * objects without an explicit namespace land in `namespace`.
   */
  async importYaml(
    cluster: string,
    namespace: string,
    yaml: string,
  ): Promise<ImportResponse> {
    return this.requestJson<ImportResponse>(
      `/native/clusters/${encodeURIComponent(cluster)}/import`,
      { method: 'POST', body: JSON.stringify({ namespace, yaml }) },
    );
  }

  /**
   * Historical CPU/memory series for a pod from the Thanos store. Values are
   * Prometheus matrix pairs: [unix seconds, decimal string]. Rejects with
   * status 404 when no metrics store is configured.
   */
  async podMetricsRange(
    cluster: string,
    namespace: string,
    name: string,
    minutes: number,
  ): Promise<{
    cpu: [number, string][];
    memory: [number, string][];
    stepSeconds: number;
  }> {
    return this.requestJson(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/namespaces/${encodeURIComponent(namespace)}/pods/${encodeURIComponent(
        name,
      )}/metrics-range?minutes=${minutes}`,
    );
  }

  async namespaceAlerts(
    cluster: string,
    namespace: string,
  ): Promise<K8sAlertsResponse> {
    return this.requestJson(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/namespaces/${encodeURIComponent(namespace)}/alerts`,
    );
  }

  // ── Per-user preferences (favorites, pinned kinds, quick-start progress,
  // first-run tour) — persisted server-side so they follow the signed-in
  // user across browsers/machines. See backend/src/plugins/k8s-console/persistence.ts.

  async getPreferences(): Promise<Preferences> {
    return this.requestJson('/native/preferences');
  }

  async addFavorite(input: {
    cluster: string;
    namespace: string;
    section: string;
    kind: string;
    name: string;
  }): Promise<FavoriteResource> {
    const body = await this.requestJson<{ favorite: FavoriteResource }>(
      '/native/preferences/favorites',
      { method: 'POST', body: JSON.stringify(input) },
    );
    return body.favorite;
  }

  async removeFavorite(key: string): Promise<void> {
    await this.request(
      `/native/preferences/favorites/${encodeURIComponent(key)}`,
      { method: 'DELETE' },
    );
  }

  async setPinnedKinds(kindIds: string[]): Promise<string[]> {
    const body = await this.requestJson<{ pinnedKinds: string[] }>(
      '/native/preferences/pinned-kinds',
      { method: 'PUT', body: JSON.stringify({ kindIds }) },
    );
    return body.pinnedKinds;
  }

  async setQuickStartProgress(id: string, step: number): Promise<void> {
    await this.request(
      `/native/preferences/quickstarts/${encodeURIComponent(id)}`,
      { method: 'PUT', body: JSON.stringify({ step }) },
    );
  }

  async clearQuickStartProgress(id: string): Promise<void> {
    await this.request(
      `/native/preferences/quickstarts/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  }

  async setTourCompleted(completed: boolean): Promise<void> {
    await this.request('/native/preferences/tour', {
      method: 'PUT',
      body: JSON.stringify({ completed }),
    });
  }

  // ── Alertmanager: alert list, silence list/create/expire ──────────────

  async alertmanagerCapability(cluster: string): Promise<AlertmanagerCapability> {
    return this.requestJson(
      `/native/clusters/${encodeURIComponent(cluster)}/alertmanager/capability`,
    );
  }

  async namespaceAlertmanagerAlerts(
    cluster: string,
    namespace: string,
  ): Promise<{ alerts: AlertmanagerAlert[] }> {
    return this.requestJson(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/namespaces/${encodeURIComponent(namespace)}/alertmanager/alerts`,
    );
  }

  async namespaceSilences(
    cluster: string,
    namespace: string,
  ): Promise<{ silences: AlertmanagerSilence[] }> {
    return this.requestJson(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/namespaces/${encodeURIComponent(namespace)}/alertmanager/silences`,
    );
  }

  async createSilence(
    cluster: string,
    input: {
      namespace: string;
      comment: string;
      durationSeconds: number;
      matchers: AlertmanagerMatcher[];
    },
  ): Promise<{ silenceId: string; link?: string }> {
    return this.requestJson(
      `/native/clusters/${encodeURIComponent(cluster)}/alertmanager/silences`,
      { method: 'POST', body: JSON.stringify(input) },
    );
  }

  async expireSilence(cluster: string, id: string): Promise<void> {
    await this.request(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/alertmanager/silences/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  }

  // ── Fixed namespace dashboard (CPU/memory/network/restarts by pod) ────

  async namespaceDashboard(
    cluster: string,
    namespace: string,
    minutes = 60,
  ): Promise<NamespaceDashboardResponse> {
    return this.requestJson(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/namespaces/${encodeURIComponent(
        namespace,
      )}/dashboard?minutes=${minutes}`,
    );
  }

  async prometheusQuery(
    cluster: string,
    input: {
      query: string;
      start?: number;
      end?: number;
      step?: number;
      time?: number;
    },
  ): Promise<unknown> {
    const params = new URLSearchParams({ query: input.query });
    if (
      input.start !== undefined &&
      input.end !== undefined &&
      input.step !== undefined
    ) {
      params.set('mode', 'range');
      params.set('start', String(input.start));
      params.set('end', String(input.end));
      params.set('step', String(input.step));
    } else if (input.time !== undefined) {
      params.set('time', String(input.time));
    }
    return this.requestJson(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/prometheus/query?${params.toString()}`,
    );
  }

  async incidentTimeline(
    cluster: string,
    input: {
      namespace: string;
      apiBase?: string;
      resource?: string;
      name?: string;
      limit?: number;
    },
  ): Promise<unknown> {
    const params = new URLSearchParams();
    if (input.apiBase) params.set('apiBase', input.apiBase);
    if (input.resource) params.set('resource', input.resource);
    if (input.name) params.set('name', input.name);
    if (input.limit) params.set('limit', String(input.limit));
    return this.requestJson(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/namespaces/${encodeURIComponent(
        input.namespace,
      )}/incident-timeline?${params.toString()}`,
    );
  }

  async gitOpsContext(
    cluster: string,
    input: {
      namespace: string;
      apiBase?: string;
      resource?: string;
      name?: string;
    },
  ): Promise<unknown> {
    const params = new URLSearchParams();
    if (input.apiBase) params.set('apiBase', input.apiBase);
    if (input.resource) params.set('resource', input.resource);
    if (input.name) params.set('name', input.name);
    return this.requestJson(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/namespaces/${encodeURIComponent(
        input.namespace,
      )}/gitops-drift?${params.toString()}`,
    );
  }

  async policyExplanation(
    cluster: string,
    input: {
      namespace: string;
      resource?: string;
      verb: string;
      group?: string;
      name?: string;
    },
  ): Promise<unknown> {
    return this.requestJson(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/namespaces/${encodeURIComponent(
        input.namespace,
      )}/policy-explanation`,
      {
        method: 'POST',
        body: JSON.stringify({
          action: {
            resource: input.resource,
            verb: input.verb,
            group: input.group,
            name: input.name,
          },
        }),
      },
    );
  }

  async quickStartPreflight(
    cluster: string,
    namespace: string,
    quickStart: string,
  ): Promise<unknown> {
    return this.requestJson(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/namespaces/${encodeURIComponent(
        namespace,
      )}/quick-starts/${encodeURIComponent(quickStart)}/preflight`,
    );
  }

  async watchResources(
    cluster: string,
    path: string,
    onEvent: (event: K8sWatchEvent) => void,
    signal: AbortSignal,
    resourceVersion?: string,
  ): Promise<void> {
    const params = new URLSearchParams({ path });
    if (resourceVersion) params.set('resourceVersion', resourceVersion);
    const response = await this.request(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/watch?${params.toString()}`,
      { signal },
    );
    if (!response.body) throw new Error('The resource watch returned no body');
    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .getReader();
    let buffer = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += value;
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
          const data = frame
            .split('\n')
            .filter(line => line.startsWith('data:'))
            .map(line => line.slice(5).trimStart())
            .join('\n');
          if (data) onEvent(JSON.parse(data) as K8sWatchEvent);
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  async followPodLogs(
    cluster: string,
    namespace: string,
    name: string,
    container: string,
    onChunk: (chunk: string) => void,
    signal: AbortSignal,
  ): Promise<void> {
    const params = new URLSearchParams({ container });
    const response = await this.request(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/namespaces/${encodeURIComponent(namespace)}/pods/${encodeURIComponent(
        name,
      )}/log-stream?${params}`,
      { signal },
    );
    if (!response.body) {
      throw new Error('The log stream returned no response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        onChunk(decoder.decode(value, { stream: true }));
      }
      const final = decoder.decode();
      if (final) onChunk(final);
    } finally {
      reader.releaseLock();
    }
  }

  async createExecSession(
    cluster: string,
    namespace: string,
    pod: string,
    container: string,
    size?: { cols: number; rows: number },
  ): Promise<{ id: string; token: string }> {
    return this.requestJson(
      `/native/clusters/${encodeURIComponent(
        cluster,
      )}/namespaces/${encodeURIComponent(namespace)}/pods/${encodeURIComponent(
        pod,
      )}/exec-sessions`,
      {
        method: 'POST',
        body: JSON.stringify({ container, ...size }),
      },
    );
  }

  async resizeExecSession(
    sessionId: string,
    sessionToken: string,
    cols: number,
    rows: number,
  ): Promise<void> {
    await this.request(
      `/native/exec-sessions/${encodeURIComponent(sessionId)}/resize`,
      {
        method: 'POST',
        body: JSON.stringify({ cols, rows }),
        headers: { 'X-Terminal-Session-Token': sessionToken },
      },
    );
  }

  async streamExecSession(
    sessionId: string,
    sessionToken: string,
    onEvent: (event: {
      type: 'stdout' | 'stderr' | 'status';
      data: string;
    }) => void,
    signal: AbortSignal,
  ): Promise<void> {
    const response = await this.request(
      `/native/exec-sessions/${encodeURIComponent(sessionId)}/events`,
      {
        signal,
        headers: { 'X-Terminal-Session-Token': sessionToken },
      },
    );
    if (!response.body) throw new Error('Terminal returned no event stream');
    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .getReader();
    let buffer = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += value;
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const event = JSON.parse(line.slice(6)) as {
            type: 'stdout' | 'stderr' | 'status';
            data: string;
          };
          onEvent(event);
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  async sendExecInput(
    sessionId: string,
    sessionToken: string,
    data: string,
  ): Promise<void> {
    await this.request(
      `/native/exec-sessions/${encodeURIComponent(sessionId)}/input`,
      {
        method: 'POST',
        body: JSON.stringify({ data }),
        headers: { 'X-Terminal-Session-Token': sessionToken },
      },
    );
  }

  async closeExecSession(
    sessionId: string,
    sessionToken: string,
  ): Promise<void> {
    await this.request(
      `/native/exec-sessions/${encodeURIComponent(sessionId)}`,
      {
        method: 'DELETE',
        headers: { 'X-Terminal-Session-Token': sessionToken },
      },
    );
  }

  async restart(
    cluster: string,
    namespace: string,
    resource: string,
    name: string,
  ): Promise<void> {
    await this.request(
      this.actionPath(cluster, namespace, resource, name, 'restart'),
      { method: 'POST' },
    );
  }

  async scale(
    cluster: string,
    namespace: string,
    resource: string,
    name: string,
    replicas: number,
  ): Promise<void> {
    await this.request(
      this.actionPath(cluster, namespace, resource, name, 'scale'),
      { method: 'POST', body: JSON.stringify({ replicas }) },
    );
  }

  /**
   * Replace an object with an edited single-document YAML manifest. Pass an
   * empty namespace for cluster-scoped kinds.
   */
  async updateYaml(
    cluster: string,
    namespace: string,
    resource: string,
    name: string,
    apiBase: string,
    yaml: string,
  ): Promise<void> {
    await this.request(
      `${this.objectPath(
        cluster,
        namespace,
        resource,
        name,
      )}?apiBase=${encodeURIComponent(apiBase)}`,
      { method: 'PUT', body: JSON.stringify({ yaml }) },
    );
  }

  /** Delete any object. Pass an empty namespace for cluster-scoped kinds. */
  async deleteObject(
    cluster: string,
    namespace: string,
    resource: string,
    name: string,
    apiBase: string,
  ): Promise<void> {
    await this.request(
      `${this.objectPath(
        cluster,
        namespace,
        resource,
        name,
      )}?apiBase=${encodeURIComponent(apiBase)}`,
      { method: 'DELETE' },
    );
  }

  private proxyPath(cluster: string, path: string): string {
    return `/native/clusters/${encodeURIComponent(cluster)}/proxy${path}`;
  }

  private objectPath(
    cluster: string,
    namespace: string,
    resource: string,
    name: string,
  ): string {
    const scope = namespace
      ? `namespaces/${encodeURIComponent(namespace)}`
      : 'cluster-resources';
    return `/native/clusters/${encodeURIComponent(
      cluster,
    )}/${scope}/${encodeURIComponent(resource)}/${encodeURIComponent(name)}`;
  }

  private actionPath(
    cluster: string,
    namespace: string,
    resource: string,
    name: string,
    action: string,
  ): string {
    return `${this.objectPath(cluster, namespace, resource, name)}/${action}`;
  }

  private async requestJson<T>(
    path: string,
    init: RequestInit = {},
  ): Promise<T> {
    const response = await this.request(path, init);
    return response.json() as Promise<T>;
  }

  private async request(
    path: string,
    init: RequestInit = {},
  ): Promise<Response> {
    const [baseUrl, accessToken] = await Promise.all([
      this.discoveryApi.getBaseUrl('k8s-console'),
      this.microsoftAuthApi.getAccessToken(AKS_SERVER_APP_SCOPE),
    ]);
    if (!accessToken) throw new Error('Microsoft Entra login is required');

    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'X-Kubernetes-Authorization': `Bearer ${accessToken}`,
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });

    if (!response.ok) {
      const text = await response.text();
      let message = `Kubernetes request failed (${response.status})`;
      try {
        const body = JSON.parse(text);
        if (typeof body.error === 'string') message = body.error;
        else if (typeof body.message === 'string') message = body.message;
      } catch {
        /* keep default message */
      }
      const error = new Error(message) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }
    return response;
  }
}

export function useKubernetesConsoleApi(): KubernetesConsoleApi {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);

  return useMemo(
    () => new KubernetesConsoleApi(discoveryApi, fetchApi, microsoftAuthApi),
    [discoveryApi, fetchApi, microsoftAuthApi],
  );
}
